/**
 * The project's audit log (`MCK-20260521-009`, C7/C9).
 *
 * **Two calls, one mapping.** `activityQuery()` is the only place the filter object becomes a
 * query string, and both the list and the CSV export go through it — which is what makes
 * *"the export respects the active filter"* structurally true rather than maintained. The
 * server holds up the same half: `_filters()` in `domains/activity/router.py` builds one
 * predicate for both handlers, so every boundary the list applies applies to the file too, and
 * an export can never carry a row the same caller could not see on screen.
 *
 * **One endpoint, and the page can be partial.** `scoped` is `true` when the caller's zones
 * bounded what came back — `MCK-20260521-005` invariant 6: a filtered log has to say it is
 * filtered, or its gaps read as an absence of events, which is the one failure an audit trail
 * cannot have. The screen prints that sentence; this file only carries the flag. The CSV says
 * it in its own first row, written server-side, because a file outlives the screen it came from.
 *
 * **The hash and the signature are not on the JSON wire, and are on the CSV's.** They are what
 * makes a row verifiable, and a log that showed everybody a hash they cannot check on a screen
 * would be theatre — a hash in an exported file is the string an auditor quotes back.
 */

import { api, unwrap, unwrapFile } from '@/api/client';
import type { components } from '@/api/generated/openapi';

import type { ActivityFilters } from './filters';

type Schemas = components['schemas'];

export type ActivityEvent = Schemas['ActivityEventResponse'];
export type ActivityPage = Schemas['Page_ActivityEventResponse_'];

/**
 * The filter object as the wire sees it — the one mapping, read by the list and by the export.
 *
 * The three any-of sets go out as **repeated** parameters, which is what the API's filter rules
 * mean by OR within one field; the URL carries them comma-joined because that is the form a
 * person can type (`./filters.ts`). The translation happens here and only here.
 *
 * TWIN: `_filters()` in `domains/activity/router.py`. A field that changes arity changes in
 * both, in one commit.
 *
 * Values are spread conditionally rather than passed as `undefined`: `exactOptionalPropertyTypes`
 * is on, and an explicit `undefined` serialises as an empty parameter — `?event_type=` is a
 * filter the server then has to decide about, and on an audit log deciding it wrong empties the
 * page.
 */
export function activityQuery(filters: ActivityFilters) {
  return {
    ...(filters.actor.length === 0 ? {} : { actor_user_id: [...filters.actor] }),
    ...(filters.eventType.length === 0 ? {} : { event_type: [...filters.eventType] }),
    ...(filters.entityType.length === 0 ? {} : { entity_type: [...filters.entityType] }),
    ...(filters.since === null ? {} : { since: filters.since }),
    ...(filters.until === null ? {} : { until: filters.until }),
    ...(filters.q === null ? {} : { q: filters.q }),
  };
}

export function readActivity(
  projectId: string,
  filters: ActivityFilters,
  cursor: string | null,
): Promise<ActivityPage> {
  return unwrap(
    api.GET('/api/v1/projects/{project_id}/activity', {
      params: {
        path: { project_id: projectId },
        query: {
          ...activityQuery(filters),
          ...(cursor === null ? {} : { cursor }),
        },
      },
    }),
  );
}

/**
 * The filter currently on screen, as a CSV file carrying each row's proof.
 *
 * The same `activityQuery()` the list sends, and no cursor: a file is not paged. The server
 * bounds it by `ACTIVITY_EXPORT_LIMIT` rather than by a page, so what comes back is the whole
 * answer to the question on screen.
 */
export function exportActivityCsv(projectId: string, filters: ActivityFilters): Promise<Blob> {
  return unwrapFile(
    api.GET('/api/v1/projects/{project_id}/activity-exports', {
      params: { path: { project_id: projectId }, query: activityQuery(filters) },
      parseAs: 'blob',
    }),
  );
}
